import {
  TRACKER_REPORT_ROWS,
  TRACKER_REPORT_STATUS_COLUMNS,
  isTrackerOpenStatus,
  resolveTrackerRowId,
  resolveTrackerStatusId,
  type TrackerReportRowId,
  type TrackerReportStatusId,
} from "@sd/lib/trackerReportConstants";
import { statusToSlug } from "@sd/constants/ticketStatuses";

export interface TrackerReportMatrix {
  cells: Record<TrackerReportRowId, Record<TrackerReportStatusId, number>>;
  rowOpen: Record<TrackerReportRowId, number>;
  rowClosed: Record<TrackerReportRowId, number>;
  rowTotal: Record<TrackerReportRowId, number>;
  colTotal: Record<TrackerReportStatusId, number>;
  summaryOpen: number;
  summaryClosed: number;
  grandTotal: number;
}

function emptyCells(): Record<TrackerReportRowId, Record<TrackerReportStatusId, number>> {
  const cells = {} as Record<TrackerReportRowId, Record<TrackerReportStatusId, number>>;
  for (const row of TRACKER_REPORT_ROWS) {
    const statusMap = {} as Record<TrackerReportStatusId, number>;
    for (const col of TRACKER_REPORT_STATUS_COLUMNS) {
      statusMap[col.id] = 0;
    }
    cells[row.id] = statusMap;
  }
  return cells;
}

function pickTrackerField(row: Record<string, unknown>): unknown {
  return (
    row.tracker ??
    row.issue_tracker ??
    row.tracker_type ??
    row.ticket_tracker ??
    row.tracker_label ??
    row.tickettype ??
    row.ticket_type ??
    row.tickittype
  );
}

function pickStatusField(row: Record<string, unknown>): unknown {
  return row.status ?? row.ticket_status ?? row.status_name ?? row.status_label;
}

function readCount(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.trunc(n);
}

function readCountValue(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) return Math.trunc(n);
  }
  return 0;
}

const TRACKER_TOTAL_KEYS = new Set([
  "total",
  "total_count",
  "count",
  "total_tickets",
  "grand_total",
  "grandtotal",
]);

function readExplicitTotalFromRecord(
  record: Record<string, unknown>
): number | undefined {
  for (const [key, value] of Object.entries(record)) {
    if (TRACKER_TOTAL_KEYS.has(statusToSlug(key))) {
      return readCountValue(value);
    }
  }
  return undefined;
}

function getMatrixRowRecords(root: Record<string, unknown>): Record<string, unknown>[] {
  for (const key of ["data", "issues_by_tracker", "tracker_rows", "rows"]) {
    const value = root[key];
    if (!Array.isArray(value)) continue;
    const list = value.filter(
      (item): item is Record<string, unknown> =>
        Boolean(item) && typeof item === "object"
    );
    if (list.length > 0) return list;
  }
  return [];
}

/** Apply backend row/column/grand totals — never re-sum when API sends them. */
function applyApiTotalsToMatrix(
  matrix: TrackerReportMatrix,
  root: Record<string, unknown> | null,
  rowRecords: Record<string, unknown>[]
): TrackerReportMatrix {
  if (!root && rowRecords.length === 0) return matrix;

  const next: TrackerReportMatrix = {
    ...matrix,
    rowTotal: { ...matrix.rowTotal },
    colTotal: { ...matrix.colTotal },
  };

  for (const record of rowRecords) {
    const rowId = resolveTrackerRowId(pickTrackerField(record));
    if (!rowId) continue;
    const rawRowTotal = record.total ?? record.Total ?? record.row_total;
    if (rawRowTotal != null) {
      next.rowTotal[rowId] = readCountValue(rawRowTotal);
    }
  }

  if (root) {
    const statusTotals =
      root.status_totals ?? root.statusTotals ?? root.column_totals;
    if (statusTotals && typeof statusTotals === "object") {
      for (const [key, value] of Object.entries(
        statusTotals as Record<string, unknown>
      )) {
        const statusId = resolveTrackerStatusId(key);
        if (statusId) next.colTotal[statusId] = readCountValue(value);
      }
    }

    const explicitGrand = readExplicitTotalFromRecord(root);
    if (explicitGrand !== undefined) {
      next.grandTotal = explicitGrand;
    }
  }

  return next;
}

function recomputeMatrixTotals(
  cells: Record<TrackerReportRowId, Record<TrackerReportStatusId, number>>
): TrackerReportMatrix {
  const rowOpen = Object.fromEntries(
    TRACKER_REPORT_ROWS.map(row => [row.id, 0])
  ) as Record<TrackerReportRowId, number>;
  const rowClosed = { ...rowOpen };
  const rowTotal = { ...rowOpen };
  const colTotal = Object.fromEntries(
    TRACKER_REPORT_STATUS_COLUMNS.map(col => [col.id, 0])
  ) as Record<TrackerReportStatusId, number>;

  for (const row of TRACKER_REPORT_ROWS) {
    for (const col of TRACKER_REPORT_STATUS_COLUMNS) {
      const count = cells[row.id][col.id] ?? 0;
      if (count <= 0) continue;
      colTotal[col.id] += count;
      rowTotal[row.id] += count;
      if (isTrackerOpenStatus(col.id)) {
        rowOpen[row.id] += count;
      } else {
        rowClosed[row.id] += count;
      }
    }
  }

  return {
    cells,
    rowOpen,
    rowClosed,
    rowTotal,
    colTotal,
    summaryOpen: TRACKER_REPORT_ROWS.reduce(
      (sum, row) => sum + rowOpen[row.id],
      0
    ),
    summaryClosed: TRACKER_REPORT_ROWS.reduce(
      (sum, row) => sum + rowClosed[row.id],
      0
    ),
    grandTotal: TRACKER_REPORT_ROWS.reduce(
      (sum, row) => sum + rowTotal[row.id],
      0
    ),
  };
}

function cloneEmptyCells(): Record<
  TrackerReportRowId,
  Record<TrackerReportStatusId, number>
> {
  return JSON.parse(JSON.stringify(emptyCells())) as ReturnType<
    typeof emptyCells
  >;
}

function applyStatusCount(
  cells: Record<TrackerReportRowId, Record<TrackerReportStatusId, number>>,
  rowId: TrackerReportRowId,
  statusId: TrackerReportStatusId,
  count: number
): void {
  if (count <= 0) return;
  cells[rowId][statusId] += count;
}

function isTrackerReportMatrixRow(record: Record<string, unknown>): boolean {
  return record.status_counts != null || record.statuses != null;
}

function applyMatrixRowRecords(
  cells: Record<TrackerReportRowId, Record<TrackerReportStatusId, number>>,
  records: Record<string, unknown>[]
): boolean {
  let applied = false;

  for (const record of records) {
    const rowId = resolveTrackerRowId(pickTrackerField(record));
    if (!rowId) continue;

    const nestedStatuses = record.statuses ?? record.status_counts;
    const hasNestedStatuses =
      nestedStatuses &&
      typeof nestedStatuses === "object" &&
      Object.keys(nestedStatuses as object).length > 0;

    if (hasNestedStatuses) {
      // Matrix row from reports API — even all-zero rows must not fall back to ticket counting.
      applied = true;
      for (const [key, value] of Object.entries(
        nestedStatuses as Record<string, unknown>
      )) {
        const statusId = resolveTrackerStatusId(key);
        const count = readCount(value);
        if (!statusId || count <= 0) continue;
        applyStatusCount(cells, rowId, statusId, count);
      }
      continue;
    }

    for (const col of TRACKER_REPORT_STATUS_COLUMNS) {
      const count = readCount(
        record[col.id] ??
          record[col.label] ??
          record[col.label.toLowerCase()]
      );
      if (count > 0) {
        applyStatusCount(cells, rowId, col.id, count);
        applied = true;
      }
    }
  }

  return applied;
}

function applyFlatCountRecords(
  cells: Record<TrackerReportRowId, Record<TrackerReportStatusId, number>>,
  records: Record<string, unknown>[]
): boolean {
  let applied = false;

  for (const record of records) {
    const rowId = resolveTrackerRowId(pickTrackerField(record));
    const statusId = resolveTrackerStatusId(pickStatusField(record));
    const count = readCount(
      record.count ?? record.total ?? record.value ?? record.ticket_count
    );
    if (!rowId || !statusId || count <= 0) continue;
    applyStatusCount(cells, rowId, statusId, count);
    applied = true;
  }

  return applied;
}

function unwrapReportsPayload(payload: unknown): unknown {
  if (!payload || typeof payload !== "object") return payload;
  const root = payload as Record<string, unknown>;
  const data = root.data;

  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    const nested = data as Record<string, unknown>;
    for (const key of [
      "issues_by_tracker",
      "tracker",
      "tracker_report",
      "matrix",
      "rows",
      "results",
      "items",
    ]) {
      const value = nested[key];
      if (Array.isArray(value)) return value;
    }
    return nested;
  }

  return payload;
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is Record<string, unknown> =>
      Boolean(item) && typeof item === "object"
  );
}

/** Detect the reports payload that carries backend-computed totals. */
function hasBackendTotals(root: Record<string, unknown>): boolean {
  if (readExplicitTotalFromRecord(root) !== undefined) return true;
  if (root.grand_total != null || root.status_totals != null) return true;
  const records = getMatrixRowRecords(root);
  return (
    records.length > 0 &&
    records.some(record => record.total != null || record.Total != null)
  );
}

/** Unwrap `{ data: { grand_total, status_totals, … } }` API envelopes. */
function resolveTrackerReportsRoot(
  payload: unknown
): Record<string, unknown> | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }

  const root = payload as Record<string, unknown>;
  if (hasBackendTotals(root)) return root;

  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const nested = data as Record<string, unknown>;
    if (hasBackendTotals(nested)) return nested;
  }

  return root;
}

/**
 * Build the matrix using backend-provided totals: per-row `total`/`open`,
 * `status_totals` (column totals) and `grand_total`. Cells still come from each
 * row's `status_counts`. No frontend re-summing of totals.
 */
function parseBackendTotalsPayload(
  root: Record<string, unknown>
): TrackerReportMatrix {
  const cells = cloneEmptyCells();
  const rowOpen = Object.fromEntries(
    TRACKER_REPORT_ROWS.map(row => [row.id, 0])
  ) as Record<TrackerReportRowId, number>;
  const rowClosed = { ...rowOpen };
  const rowTotal = { ...rowOpen };
  const colTotal = Object.fromEntries(
    TRACKER_REPORT_STATUS_COLUMNS.map(col => [col.id, 0])
  ) as Record<TrackerReportStatusId, number>;

  for (const record of getMatrixRowRecords(root)) {
    const rowId = resolveTrackerRowId(pickTrackerField(record));
    if (!rowId) continue;

    const statuses = record.status_counts ?? record.statuses;
    if (statuses && typeof statuses === "object") {
      for (const [key, value] of Object.entries(
        statuses as Record<string, unknown>
      )) {
        const statusId = resolveTrackerStatusId(key);
        const count = readCount(value);
        if (!statusId || count <= 0) continue;
        cells[rowId][statusId] += count;
      }
    }

    rowTotal[rowId] = readCountValue(record.total ?? record.Total);
    rowOpen[rowId] = readCountValue(record.open);
    rowClosed[rowId] = Math.max(0, rowTotal[rowId] - rowOpen[rowId]);
  }

  const statusTotals = root.status_totals;
  if (statusTotals && typeof statusTotals === "object") {
    for (const [key, value] of Object.entries(
      statusTotals as Record<string, unknown>
    )) {
      const statusId = resolveTrackerStatusId(key);
      if (!statusId) continue;
      colTotal[statusId] = readCountValue(value);
    }
  }

  const grandTotal =
    readExplicitTotalFromRecord(root) ?? readCountValue(root.grand_total);
  const summaryOpen = readCount(root.open);
  const summaryClosed = Math.max(0, grandTotal - summaryOpen);

  return {
    cells,
    rowOpen,
    rowClosed,
    rowTotal,
    colTotal,
    summaryOpen,
    summaryClosed,
    grandTotal,
  };
}

/**
 * Build matrix from GET `/service_desk/reports/` payload.
 */
export function parseTrackerReportsPayload(payload: unknown): TrackerReportMatrix {
  const reportsRoot = resolveTrackerReportsRoot(payload);

  if (reportsRoot && hasBackendTotals(reportsRoot)) {
    return parseBackendTotalsPayload(reportsRoot);
  }

  const outerRoot =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null;
  const totalsRoot = reportsRoot ?? outerRoot;

  const unwrapped = unwrapReportsPayload(payload);
  const cells = cloneEmptyCells();
  const recordList = asRecordArray(unwrapped);

  if (recordList.length > 0) {
    if (applyMatrixRowRecords(cells, recordList)) {
      return applyApiTotalsToMatrix(
        recomputeMatrixTotals(cells),
        totalsRoot,
        recordList
      );
    }
    if (applyFlatCountRecords(cells, recordList)) {
      return applyApiTotalsToMatrix(
        recomputeMatrixTotals(cells),
        totalsRoot,
        recordList
      );
    }
    if (recordList.some(isTrackerReportMatrixRow)) {
      return applyApiTotalsToMatrix(
        recomputeMatrixTotals(cells),
        totalsRoot,
        recordList
      );
    }
    return buildTrackerMatrixFromTickets(recordList);
  }

  if (unwrapped && typeof unwrapped === "object") {
    const objectPayload = unwrapped as Record<string, unknown>;
    const nestedList = asRecordArray(
      objectPayload.issues_by_tracker ??
        objectPayload.tracker_rows ??
        objectPayload.data
    );
    if (nestedList.length > 0) {
      if (applyMatrixRowRecords(cells, nestedList)) {
        return applyApiTotalsToMatrix(
          recomputeMatrixTotals(cells),
          totalsRoot ?? objectPayload,
          nestedList
        );
      }
      if (applyFlatCountRecords(cells, nestedList)) {
        return applyApiTotalsToMatrix(
          recomputeMatrixTotals(cells),
          totalsRoot ?? objectPayload,
          nestedList
        );
      }
      if (nestedList.some(isTrackerReportMatrixRow)) {
        return applyApiTotalsToMatrix(
          recomputeMatrixTotals(cells),
          totalsRoot ?? objectPayload,
          nestedList
        );
      }
    }
  }

  return applyApiTotalsToMatrix(
    buildTrackerMatrixFromTickets([]),
    totalsRoot,
    []
  );
}

/**
 * Build the Tracker matrix from ticket list rows (GET `/service_desk/ticket_create/`).
 */
export function buildTrackerMatrixFromTickets(rows: unknown[]): TrackerReportMatrix {
  const cells = emptyCells();
  const rowOpen = Object.fromEntries(
    TRACKER_REPORT_ROWS.map(row => [row.id, 0])
  ) as Record<TrackerReportRowId, number>;
  const rowClosed = { ...rowOpen };
  const rowTotal = { ...rowOpen };
  const colTotal = Object.fromEntries(
    TRACKER_REPORT_STATUS_COLUMNS.map(col => [col.id, 0])
  ) as Record<TrackerReportStatusId, number>;

  for (const item of rows) {
    if (!item || typeof item !== "object") continue;
    const record = item as Record<string, unknown>;

    const rowId = resolveTrackerRowId(pickTrackerField(record));
    const statusRaw = record.status ?? record.ticket_status;
    const statusId = resolveTrackerStatusId(statusRaw);
    if (!rowId || !statusId) continue;

    cells[rowId][statusId] += 1;
    colTotal[statusId] += 1;
    rowTotal[rowId] += 1;

    if (isTrackerOpenStatus(statusId)) {
      rowOpen[rowId] += 1;
    } else {
      rowClosed[rowId] += 1;
    }
  }

  const summaryOpen = TRACKER_REPORT_ROWS.reduce(
    (sum, row) => sum + rowOpen[row.id],
    0
  );
  const summaryClosed = TRACKER_REPORT_ROWS.reduce(
    (sum, row) => sum + rowClosed[row.id],
    0
  );
  const grandTotal = TRACKER_REPORT_ROWS.reduce(
    (sum, row) => sum + rowTotal[row.id],
    0
  );

  return {
    cells,
    rowOpen,
    rowClosed,
    rowTotal,
    colTotal,
    summaryOpen,
    summaryClosed,
    grandTotal,
  };
}

export function trackerMatrixToCsv(matrix: TrackerReportMatrix): string {
  const headers = [
    "Tracker",
    ...TRACKER_REPORT_STATUS_COLUMNS.map(col => col.label),
    "Total",
  ];

  const lines = [headers.join(",")];

  for (const row of TRACKER_REPORT_ROWS) {
    const values = [
      `"${row.label}"`,
      ...TRACKER_REPORT_STATUS_COLUMNS.map(col => {
        const n = matrix.cells[row.id][col.id];
        return n > 0 ? String(n) : "-";
      }),
      String(matrix.rowTotal[row.id] || 0),
    ];
    lines.push(values.join(","));
  }

  const totalRow = [
    '"Total"',
    ...TRACKER_REPORT_STATUS_COLUMNS.map(col =>
      String(matrix.colTotal[col.id] || 0)
    ),
    String(matrix.grandTotal),
  ];
  lines.push(totalRow.join(","));

  return lines.join("\n");
}

export interface TrackerStatusChartRow {
  statusId: TrackerReportStatusId;
  statusLabel: string;
  bug: number;
  new_requirement: number;
  suggestion: number;
}

/** Rows for the Issues by Status stacked bar chart (non-zero statuses only). */
export function buildTrackerStatusChartData(
  matrix: TrackerReportMatrix
): TrackerStatusChartRow[] {
  return TRACKER_REPORT_STATUS_COLUMNS.map(col => ({
    statusId: col.id,
    statusLabel: col.label,
    bug: matrix.cells.bug[col.id],
    new_requirement: matrix.cells.new_requirement[col.id],
    suggestion: matrix.cells.suggestion[col.id],
  })).filter(row => row.bug + row.new_requirement + row.suggestion > 0);
}

export interface TrackerTrackerChartRow {
  trackerId: TrackerReportRowId;
  trackerLabel: string;
  [key: string]: string | number;
}

export interface TrackerTrackerChartData {
  rows: TrackerTrackerChartRow[];
  activeStatusIds: TrackerReportStatusId[];
}

/** Rows for the Issues by Tracker stacked bar chart (non-zero trackers only). */
export function buildTrackerTrackerChartData(
  matrix: TrackerReportMatrix
): TrackerTrackerChartData {
  const rows = TRACKER_REPORT_ROWS.map(row => {
    const entry: TrackerTrackerChartRow = {
      trackerId: row.id,
      trackerLabel: row.label,
    };
    for (const col of TRACKER_REPORT_STATUS_COLUMNS) {
      entry[col.id] = matrix.cells[row.id][col.id];
    }
    return entry;
  }).filter(row =>
    TRACKER_REPORT_STATUS_COLUMNS.some(
      col => Number(row[col.id] ?? 0) > 0
    )
  );

  const activeStatusIds = TRACKER_REPORT_STATUS_COLUMNS.filter(col =>
    rows.some(row => Number(row[col.id] ?? 0) > 0)
  ).map(col => col.id);

  return { rows, activeStatusIds };
}

export function downloadTrackerCsv(matrix: TrackerReportMatrix, filename = "tracker-report.csv") {
  const blob = new Blob([trackerMatrixToCsv(matrix)], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
